import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { useIntl } from 'react-intl';
import { isDevFlag } from '@americanexpress/conditional-features';
import {
  Heading, IconButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@americanexpress/dls-react';
import { IconChevronLeft, IconChevronRight } from '@americanexpress/dls-icons';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { DEFAULT_DATE_FORMAT } from '../../constants/dateFormatConstants';
import {
  getEndDate,
  getFocusedField,
  getIsNextBtnShown,
  getIsPrevBtnShown,
  getStartDate,
} from '../../selectors/dateSelectors';
import CalendarWeek from '../CalendarWeek/CalendarWeek';
import DaysOfTheWeek from '../DaysOfTheWeek/DaysOfTheWeek';
import { useDateSelector } from '../../context/dateContext';
import { MARKET_START_DAYS } from '../../constants/marketConstants';
import { START_DATE, END_DATE } from '../../constants/dateConstants';
import calendarDayStyle from '../CalendarDay/CalendarDay.scss';
import style from './CalendarMonth.scss';

export const useScrollIntoView = ({ monthRef, shownDate }) => {
  const isMobile = useMediaBreakpoint('sm-down');
  const focusedField = useDateSelector(getFocusedField);
  const stringStartDate = useDateSelector(getStartDate);
  const stringEndDate = useDateSelector(getEndDate);
  useEffect(() => {
    if (isMobile && monthRef?.current?.scrollIntoView) {
      if (isDevFlag('mobileShowMoreDatepicker')) {
        monthRef.current.scrollIntoView();
      } else if (
        (focusedField === START_DATE
          || (focusedField === END_DATE && !stringEndDate))
        && stringStartDate
      ) {
        const luxonStartDate = DateTime.fromFormat(
          stringStartDate,
          DEFAULT_DATE_FORMAT
        );
        if (luxonStartDate?.hasSame(shownDate, 'month')) {
          monthRef.current.scrollIntoView();
        }
      } else if (stringEndDate) {
        const luxonEndDate = DateTime.fromFormat(
          stringEndDate,
          DEFAULT_DATE_FORMAT
        );
        if (luxonEndDate?.hasSame(shownDate, 'month')) {
          monthRef.current.scrollIntoView();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on render
  }, []);
};

const CalendarMonth = ({
  shownDate,
  index,
  numberOfMonthsShown,
  updateShownMonth,
}) => {
  const isPrevBtnShown = useDateSelector(getIsPrevBtnShown, shownDate);
  const isNextButtonShown = useDateSelector(getIsNextBtnShown, shownDate);

  const locale = useSelector(getActiveLocaleSelector);
  const isMobile = useMediaBreakpoint('sm-down');
  const monthRef = React.useRef();

  useScrollIntoView({
    monthRef,
    shownDate,
  });

  const { daysInMonth, year, month } = shownDate;

  const firstDayOffset = useMemo(
    () => (DateTime.local(year, month, 1).weekday - MARKET_START_DAYS[locale]) % 7,
    [year, month, locale]
  );

  const bindChangeMonth = (months) => () => {
    updateShownMonth(months);
  };

  const numberOfWeeks = useMemo(
    () => Math.ceil((daysInMonth + firstDayOffset) / 7),
    [daysInMonth, firstDayOffset]
  );

  const intl = useIntl();

  return (
    <div
      className={`automation-date-picker-month-${year}-${month} flex-column`}
      ref={monthRef}
    >
      <div
        className={clsx('flex flex-align-center flex-justify-center', {
          'margin-1-b': !isMobile,
          'margin-1': isMobile,
        })}
      >
        {!isMobile && index === 0 && (
          <div className={calendarDayStyle.tooltip}>
            <Tooltip id="prevMonthBtnAccessibilityTooltip">
              <TooltipTrigger>
                <IconButton
                  id="prevMonthBtn"
                  tabIndex={-1}
                  onClick={bindChangeMonth(-1)}
                  aria-disabled={!isPrevBtnShown}
                  screenReaderLabel={intl.formatMessage({ id: 'aria.previous_month' })}
                >
                  <IconChevronLeft
                    title={intl.formatMessage({ id: 'aria.previous_month' })}
                  />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>
                <span id="prev-button-label">
                  {intl.formatMessage({ id: 'aria.previous_month' })}
                </span>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        {!isMobile && index === numberOfMonthsShown - 1
          && <div className={style.monthEmptySpace} />}
        <Heading
          className="flex-item-grow text-align-center"
          level={3}
          variant="sans-xsmall-bold"
        >
          {shownDate.toLocaleString({
            month: 'short',
            year: 'numeric',
          })}
        </Heading>
        {!isMobile && index === 0 && <div className={style.monthEmptySpace} />}
        {!isMobile && index === numberOfMonthsShown - 1 && (
          <div className={calendarDayStyle.tooltip}>
            <Tooltip id="nextMonthBtnAccessibilityTooltip">
              <TooltipTrigger>
                <IconButton
                  id="nextMonthBtn"
                  onClick={bindChangeMonth(1)}
                  aria-disabled={!isNextButtonShown}
                  screenReaderLabel={intl.formatMessage({ id: 'aria.next_month' })}
                >
                  <IconChevronRight
                    title={intl.formatMessage({ id: 'aria.next_month' })}
                  />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>
                <span id="next-button-label">
                  {intl.formatMessage({ id: 'aria.next_month' })}
                </span>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
      {!isMobile && <DaysOfTheWeek className="margin-1-b" />}
      {[...new Array(numberOfWeeks).keys()].map((weekIndex) => (
        <CalendarWeek
          key={weekIndex}
          year={year}
          month={month}
          firstDayOffset={firstDayOffset}
          daysInMonth={daysInMonth}
          weekIndex={weekIndex}
        />
      ))}
    </div>
  );
};

CalendarMonth.propTypes = {
  shownDate: PropTypes.shape({
    daysInMonth: PropTypes.number,
    year: PropTypes.number,
    month: PropTypes.number,
    toFormat: PropTypes.func,
    startOf: PropTypes.func,
  }).isRequired,
  updateShownMonth: PropTypes.func,
  index: PropTypes.number.isRequired,
  numberOfMonthsShown: PropTypes.number,
};

CalendarMonth.defaultProps = {
  numberOfMonthsShown: 2,
};

export default React.memo(CalendarMonth);
